import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { useAppDispatch } from '@store/store';

import { viewOnMap, viewSiteOnMap } from '@slices/map';

import { ITitle } from '@interfaces/common/common';

import { routes } from '@constants/routes';
import { columnSizes } from '@constants/table';
import { editTooltipText, removeTooltipText, viewOnMapTooltipText, viewTooltipText } from './constants';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';
import { profilePath } from '@components/routes/AdminPanel/AssetsList/constants';

import styles from '@components/common/Table/Table.module.scss';

const { adminPanel: { assets, index }, map } = routes;

interface IGetColumns {
	openDeleteConfirm: () => void;
	setSingleSelectedSiteId: React.Dispatch<React.SetStateAction<string | null>>;
	isEditAccess: boolean;
	toggleSiteModal: () => void;
	toggleLinkModal: () => void;
	setEditableSite: React.Dispatch<React.SetStateAction<object | null>>;
	toggleUnlinkConfirm: () => void;
	isManageAssetAccess: boolean;
	navigate: NavigateFunction;
	openProfileHandler: (id: string) => void;
	verifyCoordHandler: (data: string[]) => void;
	isLinkAccess: boolean;
	isInstalledOnAssetColumnShown: boolean;
}

export const getColumns = ({
	isEditAccess,
	isLinkAccess,
	isManageAssetAccess,
	navigate,
	openDeleteConfirm,
	openProfileHandler,
	setEditableSite,
	setSingleSelectedSiteId,
	toggleLinkModal,
	toggleSiteModal,
	toggleUnlinkConfirm,
	verifyCoordHandler,
	isInstalledOnAssetColumnShown,
}: IGetColumns) => ([
	{
		header: 'Site ID',
		accessorKey: 'siteId',
		cell: ({ getValue }: CellContext<any, any>) => {
			const siteId = getValue();

			return <AdaptiveTableCell text={siteId} />;
		},
	},
	{
		header: 'Vendor',
		accessorKey: 'vendor',
		size: columnSizes.size160,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'Latitude',
		accessorKey: 'lat',
		size: columnSizes.size140,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'Longitude',
		accessorKey: 'lng',
		size: columnSizes.size140,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'site type/civil model',
		accessorKey: 'siteType',
		id: 'siteTypeName',
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value?.title} />;
		},
	},
	{
		header: 'Technology',
		accessorKey: 'technologies',
		size: columnSizes.size130,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();
			const technologies = value?.map((technology: ITitle) =>
				technology?.title).join('/');

			return <AdaptiveTableCell text={technologies || 'N/A'} />;
		},
	},
	{
		header: 'installed on asset',
		accessorKey: 'assetId',
		enableSorting: false,
		isHidden: !isInstalledOnAssetColumnShown,
		cell: function Cell({ getValue }: CellContext<any, any>) {
			const dispatch = useAppDispatch();
			const value = getValue();
			const installedOnAssetText = value ? 'yes' : 'no';

			const openAssetProfileHandler = () => navigate(`${index}${assets}/${profilePath}/${value}`);
			const viewOnMapAssetHandler = () => {
				void dispatch(viewOnMap({ assetId: value }));
				navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
			};

			return (isManageAssetAccess && value
					? <div className={styles.installedOnAsset}>
						<div onClick={openAssetProfileHandler}>
							<span>View Asset profile</span>&nbsp;{Icons.locationLink()}
						</div>
						<div className={styles.separator}>/</div>
						<div onClick={viewOnMapAssetHandler}>
							<span>{viewOnMapTooltip}</span>&nbsp;{Icons.viewLink()}
						</div>
					</div>
					: <AdaptiveTableCell text={installedOnAssetText} />
			);
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		size: columnSizes.size100,
		enableSorting: false,
		cell: function Cell({ row }: CellContext<any, any>) {
			const dispatch = useAppDispatch();

			const isTechnologies = !!row?.original?.technologies?.length;
			const isViewOnMapAvailable = !!row?.original?.lat && !!row?.original?.lng;

			const handleView = () => openProfileHandler(row?.original?.id);

			const handleDelete = () => {
				setSingleSelectedSiteId(row?.original?.id);
				openDeleteConfirm();
			};

			const handleEdit = () => {
				setEditableSite(row?.original);
				toggleSiteModal();
			};

			const handleLink = () => {
				setSingleSelectedSiteId(row?.original?.id);
				toggleLinkModal();
			};

			const handleUnlink = () => {
				setSingleSelectedSiteId(row?.original?.id);
				toggleUnlinkConfirm();
			};

			const handleCheckCoord = () => {
				verifyCoordHandler([row?.original?.id]);
			};

			const handleViewOnMap = async () => {
				if (!isTechnologies) return;

				await dispatch(viewSiteOnMap(row?.original?.id));
				navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
			};

			const linkedToSite = !!row?.original?.assetId;

			return (
				<Actions
					deleteTooltip={removeTooltipText}
					editHandler={handleEdit}
					editTooltip={editTooltipText}
					handleDelete={handleDelete}
					isDelete={isEditAccess}
					isEdit={isEditAccess}
					isLink={isLinkAccess}
					isVerifyCoordinates={linkedToSite && isLinkAccess}
					isView
					isViewOnMap={isTechnologies && isViewOnMapAvailable}
					linkHandler={linkedToSite ? handleUnlink : handleLink}
					linkTooltip={linkedToSite ? 'Unlink from the Asset' : 'Link to the Asset'}
					linkedToSite={linkedToSite}
					template='auto'
					verifyCoordinatesHandler={linkedToSite ? handleCheckCoord : undefined}
					verifyCoordinatesTooltip='Verify Site coordinates'
					viewHandler={handleView}
					viewOnMapHandler={handleViewOnMap}
					viewOnMapTooltip={viewOnMapTooltipText}
					viewTooltip={viewTooltipText}
				/>
			);
		},
	},
]);
